import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { AdvisoryContent } from '../global-types/AdvisoryContent';

const REQUEST_TIMEOUT = 30000;

async function getCall<T>(baseUrl: string | null, resourcePath: string): Promise<T> {
  if (baseUrl === null) throw new Error('Missing baseUrl value');

  const uri = `${baseUrl}${resourcePath}`;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    const response = await fetch(
      uri,
      {
        signal: controller.signal,
        credentials: 'include'
      }
    );
    clearTimeout(timeoutId);

    if (!response.ok) {
      const msg = await response.text();
      window.applicationInsights.trackTrace(
        {
          message: `API Error ${response.statusText} (${response.status}) calling '${uri}' with error message: '${msg}')`,
          severityLevel: SeverityLevel.Warning
        }
      );

      return undefined as T;
    }

    return await response.json() as T;
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        window.applicationInsights.trackTrace(
          {
            message: `Abort Error due to timeout calling '${uri}' with error message: '${error.message}'`,
            severityLevel: SeverityLevel.Error
          }
        );
      } else {
        window.applicationInsights.trackTrace(
          {
            message: `Generic Error calling '${uri}' with error message: '${error.message}'`,
            severityLevel: SeverityLevel.Error
          }
        );
      }
    } else {
      window.applicationInsights.trackTrace(
        {
          message: `Unknown Catch calling '${uri}' with object: '${error}'`,
          severityLevel: SeverityLevel.Error
        }
      );
    }

    return undefined as T;
  }
}

function getCallMock<T>(baseUrl: string | null, resourcePath: string): T {
  if (baseUrl === null) throw new Error('Missing baseUrl value');

  if (resourcePath === null) throw new Error('Missing resourcePath value');

  const element = document.getElementById('select-customer-segment') as HTMLInputElement;
  const content = document.getElementById('template-content') as HTMLInputElement;

  return element.value === 'Error'
    ? undefined as T
    : {
      AdvisoryPersonId: '12345',
      CustomerSegment: element.value,
      Content: content.value ? JSON.parse(content.value) : undefined
    } as T;
}

async function handleCallbackUrl(
  baseUrl: string | null,
  callbackPath: string,
  requestTimeout: number = REQUEST_TIMEOUT
) {
  const uri = baseUrl ? `${baseUrl}${callbackPath}` : callbackPath;

  try {
    if (!callbackPath.includes('advisoryQuoteId')) {
      navigator.sendBeacon(uri);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

    const response = await fetch(uri, { method: 'POST', signal: controller.signal });
    clearTimeout(timeoutId);
    return response.status;
  } catch (error: unknown) {
    if (error instanceof Error) {
      window
      .applicationInsights
      .trackTrace(
        {
          message: `Generic Error calling '${uri}' with error message: '${error.message}'`,
          severityLevel: SeverityLevel.Error
        }
      );
    } else {
      window.applicationInsights.trackTrace(
        {
          message: `Unknown Catch calling '${uri}' with object: '${error}'`,
          severityLevel: SeverityLevel.Error
        }
      );
    }

    return undefined;
  }
}

async function handleCallback(
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any,
  requestTimeout: number = REQUEST_TIMEOUT
) {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

    const response = await fetch(url, { method: 'POST', body: JSON.stringify(payload), signal: controller.signal });
    clearTimeout(timeoutId);
    return response.status;
  } catch (error: unknown) {
    if (error instanceof Error) {
      window
      .applicationInsights
      .trackTrace(
        {
          message: `Generic Error calling '${url}' with error message: '${error.message}'`,
          severityLevel: SeverityLevel.Error
        }
      );
    } else {
      window.applicationInsights.trackTrace(
        {
          message: `Unknown Catch calling '${url}' with object: '${error}'`,
          severityLevel: SeverityLevel.Error
        }
      );
    }

    return undefined;
  }
}

async function handleContentUrl(
  contentPath: string,
  requestTimeout: number = REQUEST_TIMEOUT
): Promise<AdvisoryContent | undefined> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

    const response = await fetch(contentPath, { method: 'POST', signal: controller.signal });
    clearTimeout(timeoutId);
    return await response.json();
  } catch (error: unknown) {
    if (error instanceof Error) {
      window
      .applicationInsights
      .trackTrace(
        {
          message: `Generic Error calling '${contentPath}' with error message: '${error.message}'`,
          severityLevel: SeverityLevel.Error
        }
      );
    } else {
      window.applicationInsights.trackTrace(
        {
          message: `Unknown Catch calling '${contentPath}' with object: '${error}'`,
          severityLevel: SeverityLevel.Error
        }
      );
    }

    return undefined;
  }
}

export default {
  getCall,
  getCallMock,
  handleCallbackUrl,
  handleCallback,
  handleContentUrl
};
